import { setMain, portalNav } from "../components/shell";
import { login, requireRole, getSession } from "../lib/auth";
import { navigate, currentPath } from "../lib/router";
import { site, horse, races, membersSeed, formatGBP, formatDate } from "../lib/content";

const memberLinks = [
  { href: "/members", label: "My shares" },
  { href: "/members/prize", label: "Prize ledger" },
  { href: "/members/diary", label: "Race diary" },
  { href: "/members/documents", label: "Docs" },
  { href: "/members/ballot", label: "Tickets / ballots" },
];

function gate(): boolean {
  const s = requireRole("member");
  if (!s) {
    navigate("/members/login", true);
    return false;
  }
  return true;
}

function wrap(title: string, body: string): void {
  const path = currentPath();
  const session = getSession()!;
  setMain(`
    <section class="page-hero" style="padding-bottom:0.5rem">
      <div class="container">
        <div class="kicker" style="color:var(--pota-secondary)">Members area · demo</div>
        <h1>${title}</h1>
        <p class="lede">Signed in as <strong>${session.name}</strong> (${session.email})</p>
        ${portalNav(memberLinks, path)}
      </div>
    </section>
    <section class="section section--tight">
      <div class="container">${body}</div>
    </section>
  `);
  document.title = `${title} — Members — ${site.club.name}`;
}

export function pageMembersLogin(): void {
  if (getSession()) {
    navigate(getSession()!.role === "admin" ? "/admin" : "/members", true);
    return;
  }
  setMain(`
    <section class="section">
      <div class="container" style="max-width:28rem;margin:2rem auto">
        <div class="card form">
          <div class="kicker" style="color:var(--pota-secondary)">Members</div>
          <h1 style="font-size:2rem">Member login</h1>
          <p style="color:var(--pota-muted)">Demo auth only — credentials are hardcoded and stored in localStorage.</p>
          <div class="callout" style="font-size:0.9rem">
            <strong>Demo:</strong> member@pieceoftheaction.demo / member123<br>
            Admin can also use this portal after admin login.
          </div>
          <form id="member-login" class="form">
            <div class="field">
              <label for="email">Email</label>
              <input id="email" name="email" type="email" required value="member@pieceoftheaction.demo" />
            </div>
            <div class="field">
              <label for="password">Password</label>
              <input id="password" name="password" type="password" required value="member123" />
            </div>
            <button class="btn btn--primary btn--block" type="submit">Sign in</button>
            <div id="login-msg" hidden></div>
          </form>
        </div>
      </div>
    </section>
  `);
  document.title = `Member login — ${site.club.name}`;
  document.getElementById("member-login")?.addEventListener("submit", (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const user = login(String(fd.get("email")), String(fd.get("password")));
    const box = document.getElementById("login-msg")!;
    if (!user) {
      box.hidden = false;
      box.className = "form-msg form-msg--err";
      box.textContent = "Invalid demo credentials.";
      return;
    }
    navigate(user.role === "admin" ? "/admin" : "/members");
  });
}

export function pageMembersDashboard(): void {
  if (!gate()) return;
  const share = membersSeed.memberShare;
  const updates = membersSeed.updates;
  wrap(
    "My shares",
    `
    <div class="callout" style="margin-bottom:1.25rem">${site.disclaimers.banner}</div>
    <div class="stat-grid" style="margin-bottom:1.5rem">
      <div class="stat"><div class="stat__label">Share tier</div><div class="stat__value" style="font-size:1.3rem">${share.tierLabel}</div></div>
      <div class="stat"><div class="stat__label">Keep (pcm)</div><div class="stat__value" style="font-size:1.3rem">${formatGBP(share.pcm)}</div></div>
      <div class="stat"><div class="stat__label">Status</div><div class="stat__value" style="font-size:1.3rem">${share.status}</div></div>
      <div class="stat"><div class="stat__label">Renews</div><div class="stat__value" style="font-size:1.3rem">${formatDate(share.renews)}</div></div>
    </div>
    <div class="two-col">
      <div class="card">
        <div class="card__eyebrow">Syndicate horse</div>
        <h2 style="margin:0.25rem 0">${share.horse}</h2>
        <p style="color:var(--pota-muted)">You hold a <strong>${share.sharePercent}%</strong> share entitlement — co-owner by %. Public registered owner on cards: <strong>${share.publicOwner}</strong>.</p>
        <p style="color:var(--pota-muted)">Prize money is distributed <strong>annually by share %</strong>. Amounts are not guaranteed.</p>
        <div class="row" style="margin-top:1rem">
          <a class="btn btn--primary btn--sm" href="/members/prize">Open prize ledger</a>
          <a class="btn btn--ghost btn--sm" href="/members/diary">Race diary</a>
        </div>
      </div>
      <div>
        <h2>Latest updates</h2>
        <div class="timeline">
          ${updates
            .map(
              (u) => `
            <article class="card timeline-item">
              <div class="timeline-item__date">${formatDate(u.date)}</div>
              <h3 style="margin:0.25rem 0">${u.title}</h3>
              <p style="margin:0;color:var(--pota-muted)">${u.body}</p>
            </article>`
            )
            .join("")}
        </div>
      </div>
    </div>
  `
  );
}

export function pageMembersPrize(): void {
  if (!gate()) return;
  const ledger = membersSeed.prizeLedger;
  const summary = ledger.summary;
  wrap(
    ledger.title,
    `
    <div class="callout" style="margin-bottom:1.25rem">${ledger.note}</div>
    <div class="stat-grid" style="margin-bottom:1.5rem">
      <div class="stat"><div class="stat__label">Year</div><div class="stat__value" style="font-size:1.3rem">${ledger.year}</div></div>
      <div class="stat"><div class="stat__label">Your share</div><div class="stat__value" style="font-size:1.3rem">${ledger.sharePercent}%</div></div>
      <div class="stat"><div class="stat__label">Entitlement YTD</div><div class="stat__value" style="font-size:1.05rem">${summary.entitlementYtdStub}</div></div>
      <div class="stat"><div class="stat__label">Next distribution</div><div class="stat__value" style="font-size:1.05rem">${summary.nextDistribution}</div></div>
    </div>
    <div class="table-wrap">
      <table class="data">
        <thead>
          <tr><th>Period</th><th>Description</th><th>Gross (horse)</th><th>Your %</th><th>Entitlement</th><th>Status</th></tr>
        </thead>
        <tbody>
          ${ledger.entries
            .map(
              (e) => `
            <tr>
              <td>${e.period}</td>
              <td>${e.description}</td>
              <td>${e.grossHorsePrizeApprox}</td>
              <td>${e.yourSharePercent}%</td>
              <td>${e.entitlementStub}</td>
              <td><span class="pill pill--navy">${e.status}</span></td>
            </tr>`
            )
            .join("")}
        </tbody>
      </table>
    </div>
    <p style="margin-top:1rem;color:var(--pota-muted);font-size:0.95rem">Last distribution: ${summary.lastDistribution}. Racing involves risk — returns are not guaranteed.</p>
  `
  );
}

export function pageMembersHorse(): void {
  if (!gate()) return;
  navigate("/members", true);
}

export function pageMembersDiary(): void {
  if (!gate()) return;
  wrap(
    `Race diary — ${horse.name}`,
    `
    <p>${races.intro}</p>
    <div class="callout" style="margin:1rem 0">Co-owners by share %. Prize money distributed annually by %. Public registered owner: Piece of the Action Racing.</div>
    <div class="table-wrap">
      <table class="data">
        <thead>
          <tr><th>Date</th><th>Course</th><th>Race</th><th>Cl</th><th>Pos</th><th>SP</th><th>Notes</th></tr>
        </thead>
        <tbody>
          ${races.entries
            .map((r) => {
              const row = r as {
                date: string;
                course: string;
                race: string;
                class?: string;
                pos?: string;
                sp?: string;
                status: string;
                notes: string;
              };
              const posPill =
                row.status === "Won" || row.status === "1st"
                  ? "pill--green"
                  : row.status === "2nd"
                    ? "pill--gold"
                    : "pill--navy";
              return `
            <tr>
              <td>${formatDate(row.date)}</td>
              <td>${row.course}</td>
              <td>${row.race}</td>
              <td>${row.class ? `Cl${row.class}` : "—"}</td>
              <td><span class="pill ${posPill}">${row.pos ?? row.status}</span></td>
              <td>${row.sp ?? "—"}</td>
              <td>${row.notes}</td>
            </tr>`;
            })
            .join("")}
        </tbody>
      </table>
    </div>
    ${
      races.lookingAhead
        ? `<div class="callout" style="margin-top:1.25rem"><strong>Looking ahead:</strong> ${races.lookingAhead}</div>`
        : ""
    }
  `
  );
}

export function pageMembersDocuments(): void {
  if (!gate()) return;
  wrap(
    "Documents",
    `
    <p>Stub document list — downloads are not wired.</p>
    <div class="grid-3">
      ${membersSeed.documents
        .map(
          (d) => `
        <article class="card">
          <div class="card__eyebrow">${d.type}</div>
          <h3>${d.title}</h3>
          <p style="color:var(--pota-muted);margin:0 0 0.75rem">${formatDate(d.date)}</p>
          <button type="button" class="btn btn--sm btn--ghost" disabled>Download (stub)</button>
        </article>`
        )
        .join("")}
    </div>
  `
  );
}

export function pageMembersBallot(): void {
  if (!gate()) return;
  const b = membersSeed.ballot;
  wrap(
    "Tickets / ballots",
    `
    <div class="card" style="max-width:36rem">
      <div class="card__eyebrow">Owners’ & trainers’ access</div>
      <h2 style="font-size:1.75rem">${b.title}</h2>
      <p><span class="pill pill--navy">${b.status}</span></p>
      <dl>
        <dt><strong>Race</strong></dt><dd>${b.race}</dd>
        <dt><strong>Opens</strong></dt><dd>${b.opens}</dd>
        <dt><strong>Places</strong></dt><dd>${b.places}</dd>
      </dl>
      <p style="color:var(--pota-muted)">${b.note}</p>
      <button type="button" class="btn btn--primary" id="ballot-enter">Enter ballot (stub)</button>
      <div id="ballot-msg" hidden style="margin-top:0.75rem"></div>
    </div>
  `
  );
  document.getElementById("ballot-enter")?.addEventListener("click", () => {
    localStorage.setItem(
      "pota.ballot",
      JSON.stringify({ entered: true, at: new Date().toISOString() })
    );
    const box = document.getElementById("ballot-msg")!;
    box.hidden = false;
    box.className = "form-msg form-msg--ok";
    box.textContent = "Interest recorded in this browser only. No real ballot ran.";
  });
}
